/**
 * useSplitLineDrawing hook
 *
 * Manages an OpenLayers Draw interaction in 'LineString' mode for the polygon
 * split tool. When the split tool is active, this hook adds a Draw interaction
 * to the map that allows the user to draw a line across the selected polygon.
 * On drawend, the line coordinates are dispatched to Redux state.
 *
 * **Validates: Requirement 14.1** – WHEN a Registered_User selects a polygon
 * Feature and activates split tool, THE Vector_Editor SHALL enable drawing
 * a split line.
 */

import { useEffect, useRef } from 'react';
import Draw from 'ol/interaction/Draw';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Stroke } from 'ol/style';
import { LineString } from 'ol/geom';
import type OLMap from 'ol/Map';
import { toLonLat } from 'ol/proj';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectIsSplitToolActive,
  setSplitLineCoordinates,
  deactivateNodeEditing,
} from '../store/slices/geometryEditingSlice';
import { stopDigitization } from '../store/slices/digitizationSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { setMeasurementMode } from '../store/slices/measurementSlice';

export interface UseSplitLineDrawingOptions {
  /** Live map value; preferred so restored modes bind after map initialization. */
  map?: OLMap | null;
  /** Backward-compatible ref for existing consumers. */
  mapRef?: React.RefObject<OLMap | null>;
}

const splitLineStyle = new Style({
  stroke: new Stroke({
    color: '#ff1744',
    width: 2.5,
    lineDash: [8, 6],
  }),
});

export function useSplitLineDrawing({ map, mapRef }: UseSplitLineDrawingOptions): void {
  const dispatch = useAppDispatch();
  const isSplitToolActive = useAppSelector(selectIsSplitToolActive);
  const digitizationMode = useAppSelector((state) => state.digitization?.mode ?? 'idle');
  const activeDrawingTool = useAppSelector((state) => state.drawing?.activeTool ?? null);
  const measurementMode = useAppSelector((state) => state.measurement?.activeMode ?? null);
  const isEditingGeometry = useAppSelector(
    (state) => state.geometryEditing?.isEditingGeometry ?? false
  );
  const liveMap = map ?? mapRef?.current ?? null;

  const drawInteractionRef = useRef<Draw | null>(null);
  const splitLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const splitSourceRef = useRef<VectorSource | null>(null);

  // Activating split is a centralized transition away from competing map modes.
  useEffect(() => {
    if (!isSplitToolActive) return;
    dispatch(stopDigitization());
    dispatch(setDrawingTool(null));
    dispatch(setMeasurementMode(null));
    dispatch(deactivateNodeEditing());
  }, [isSplitToolActive, dispatch]);

  useEffect(() => {
    if (!liveMap) return;

    const cleanup = () => {
      if (drawInteractionRef.current) {
        liveMap.removeInteraction(drawInteractionRef.current);
        drawInteractionRef.current = null;
      }
      if (splitLayerRef.current) {
        liveMap.removeLayer(splitLayerRef.current);
        splitLayerRef.current = null;
      }
      if (splitSourceRef.current) {
        splitSourceRef.current.clear();
        splitSourceRef.current = null;
      }
      const target = liveMap.getTarget();
      if (target && typeof target !== 'string') {
        (target as HTMLElement).style.cursor = '';
      }
    };

    const ownsMap =
      isSplitToolActive &&
      digitizationMode === 'idle' &&
      activeDrawingTool === null &&
      measurementMode === null &&
      !isEditingGeometry;

    if (!ownsMap) {
      cleanup();
      return;
    }

    const splitSource = new VectorSource();
    const splitLayer = new VectorLayer({
      source: splitSource,
      style: splitLineStyle,
      zIndex: 9999,
    });

    liveMap.addLayer(splitLayer);
    splitSourceRef.current = splitSource;
    splitLayerRef.current = splitLayer;

    const draw = new Draw({
      source: splitSource,
      type: 'LineString',
      style: splitLineStyle,
    });

    const target = liveMap.getTarget();
    if (target && typeof target !== 'string') {
      (target as HTMLElement).style.cursor = 'crosshair';
    }

    draw.on('drawend', (event) => {
      const geometry = event.feature.getGeometry() as LineString;
      const mapCoordinates = geometry.getCoordinates();
      const lonLatCoordinates = mapCoordinates.map((coord) => toLonLat(coord) as number[]);

      dispatch(setSplitLineCoordinates(lonLatCoordinates));
      setTimeout(() => {
        splitSourceRef.current?.clear();
      }, 500);
    });

    liveMap.addInteraction(draw);
    drawInteractionRef.current = draw;

    return cleanup;
  }, [
    liveMap,
    isSplitToolActive,
    digitizationMode,
    activeDrawingTool,
    measurementMode,
    isEditingGeometry,
    dispatch,
  ]);
}

export default useSplitLineDrawing;
